import React, { useEffect, useRef, useState } from 'react';
import { Input, InputRef, Space, Tag, theme } from 'antd';
import { CloseCircleOutlined, TagOutlined } from '@ant-design/icons';

interface TagsProps {
  tagsData: (string | null)[] | [];
  onTagsChange?: (newTags: string[]) => Promise<boolean>;
  readonly?: boolean;
}

export const Tags: React.FC<TagsProps> = ({ readonly = false, ...props }) => {
  const [tags, setTags] = useState<string[]>(props.tagsData.filter((tag) => tag !== null) as string[]);
  const [newTag, setNewTag] = useState('');
  const [isRemoving, setIsRemoving] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [inputVisible, setInputVisible] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const {
    token: { colorText }
  } = theme.useToken();

  useEffect(() => {
    if (inputVisible) {
      inputRef.current?.focus();
    }
  }, [inputVisible]);

  const handleRemove = async (removedTag: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setIsRemoving(removedTag);
    const newTags = tags.filter((tag) => tag !== removedTag);
    if (props.onTagsChange) {
      const success = await props.onTagsChange(newTags);
      if (success) {
        setTags(newTags);
      }
      setIsRemoving(null);
    }
  };

  const showInput = () => {
    setInputVisible(true);
  };

  const handleAddTag = async () => {
    // Prevent adding empty tags
    if (!newTag.trim()) {
      setNewTag('');
      setIsAdding(false);
      setInputVisible(false);
      return;
    }

    // prevent adding duplicate tags
    if (tags.includes(newTag.trim())) {
      setNewTag('');
      setIsAdding(false);
      setInputVisible(false);
      return;
    }
    setIsAdding(true);
    const newTags = [...tags, newTag.trim()];

    if (props.onTagsChange) {
      const success = await props.onTagsChange(newTags);
      if (success) {
        setTags(newTags);
      }
      setNewTag('');
      setIsAdding(false);
      setInputVisible(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewTag(e.target.value);
  };

  const handleInputKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setIsAdding(true);
      await handleAddTag();
    }
  };

  const tagInputStyle: React.CSSProperties = {
    width: 100,
    height: 22,
    marginInlineEnd: 8,
    verticalAlign: 'top'
  };

  const tagPlusStyle: React.CSSProperties = {
    height: 22,
    borderStyle: 'dashed'
  };

  return props.tagsData.length === 0 && readonly === true ? null : (
    // hide scroll bar width for it obscures the tags
    <div style={{ width: '100%', overflow: 'auto', scrollbarWidth: 'none' }}>
      <Space size="small">
        {readonly === false && (
          <>
            <TagOutlined />
            {inputVisible ? (
              <Input
                ref={inputRef}
                type="text"
                size="small"
                style={tagInputStyle}
                value={newTag}
                onChange={handleInputChange}
                onBlur={handleAddTag}
                onKeyDown={handleInputKeyDown}
                disabled={isAdding}
              />
            ) : (
              <Tag style={tagPlusStyle} onClick={showInput}>
                Click to add tag
              </Tag>
            )}
          </>
        )}

        {tags.map((tag) => (
          <Tag color="orange" key={tag} style={{ userSelect: 'none', display: 'inline-flex' }}>
            {readonly === false && <CloseCircleOutlined onClick={(e) => handleRemove(tag, e)} style={{ cursor: 'pointer' }} spin={isRemoving === tag} />}
            <span style={{ color: colorText }}>{tag}</span>
          </Tag>
        ))}
      </Space>
    </div>
  );
};
